import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ENTITY_SELECT_LIST_PARAMS } from "@/core/api/use-paged-select-options";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreateMerchantSalesAgent } from "@/features/merchant/api/use-create-merchant-sales-agent";
import type { CreateMerchantSalesAgentPayload } from "@/features/merchant/api/dto";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";
import {
  createMerchantSalesAgentSchema,
  type MerchantSalesAgentFormValues,
} from "@/features/merchant/schemas/merchant-sales-agent.schema";
import { useMerchantStores } from "@/features/merchant/api/use-merchant-stores";
import { usePartySelectOptions } from "@/features/shared/api";

const EMPTY_VALUES: MerchantSalesAgentFormValues = {
  storeId: "",
  partyId: "",
  keycloakUserId: "",
  fullName: "",
};

const toPayload = (
  values: MerchantSalesAgentFormValues
): CreateMerchantSalesAgentPayload => ({
  storeId: values.storeId?.trim() || undefined,
  partyId: values.partyId.trim(),
  keycloakUserId: values.keycloakUserId.trim(),
  fullName: values.fullName.trim(),
});

/**
 * Formulaire de création d'un agent de vente
 * (`POST /MerchantSalesAgents/merchant/{merchantId}/add`). Preview
 * MutationConfirmPreview avant l'appel API (pas de frais).
 */
export function SalesAgentFormDialog({
  open,
  onOpenChange,
  merchantId,
  actingMerchantId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantId: string;
  actingMerchantId?: string;
}) {
  const { t } = useTypedTranslation();
  const { t: tm } = useMerchantTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, le schéma zod traduit est reconstruit à chaque changement de langue.
  const schema = useMemo(() => createMerchantSalesAgentSchema(), [i18n.language]);

  const createAgent = useCreateMerchantSalesAgent(merchantId);
  const pending = createAgent.isPending;
  const [preview, setPreview] = useState<CreateMerchantSalesAgentPayload | null>(
    null
  );
  const { options: partyOptions, isLoading: partiesLoading } =
    usePartySelectOptions(open);
  const { data: stores, isLoading: storesLoading } = useMerchantStores(
    merchantId,
    ENTITY_SELECT_LIST_PARAMS,
    open
  );
  const storeOptions = useMemo(
    () =>
      (stores?.items ?? [])
        .filter((store) => Boolean(store.id))
        .map((store) => ({
          value: store.id as string,
          label: [store.name, store.address].filter(Boolean).join(" · "),
          description: store.id,
        })),
    [stores]
  );

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  const fieldError =
    (name: keyof MerchantSalesAgentFormValues) =>
    (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{tm("merchant.salesAgents.create")}</DialogTitle>
        </DialogHeader>
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field
            name="fullName"
            validators={{
              onChange: fieldError("fullName"),
              onSubmit: fieldError("fullName"),
            }}
          >
            {(field) => (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="agent-full-name">
                  {tm("merchant.salesAgents.form.fullName")}
                </Label>
                <Input
                  id="agent-full-name"
                  value={field.state.value}
                  placeholder={tm(
                    "merchant.salesAgents.form.fullNamePlaceholder"
                  )}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>
          <form.Field
            name="partyId"
            validators={{
              onChange: fieldError("partyId"),
              onSubmit: fieldError("partyId"),
            }}
          >
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="agent-party-id">
                  {tm("merchant.salesAgents.form.partyId")}
                </Label>
                <EntitySearchableSelect
                  id="agent-party-id"
                  options={partyOptions}
                  value={field.state.value}
                  onValueChange={(value) => field.handleChange(value)}
                  placeholder={tm(
                    "merchant.salesAgents.form.partyIdPlaceholder"
                  )}
                  isLoading={partiesLoading}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>
          <form.Field
            name="keycloakUserId"
            validators={{
              onChange: fieldError("keycloakUserId"),
              onSubmit: fieldError("keycloakUserId"),
            }}
          >
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="agent-keycloak-id">
                  {tm("merchant.salesAgents.form.keycloakUserId")}
                </Label>
                <Input
                  id="agent-keycloak-id"
                  className="notranslate"
                  value={field.state.value}
                  placeholder={tm(
                    "merchant.salesAgents.form.keycloakUserIdPlaceholder"
                  )}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>
          <form.Field
            name="storeId"
            validators={{
              onChange: fieldError("storeId"),
              onSubmit: fieldError("storeId"),
            }}
          >
            {(field) => (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="agent-store-id">
                  {tm("merchant.salesAgents.form.storeId")}
                </Label>
                <EntitySearchableSelect
                  id="agent-store-id"
                  options={storeOptions}
                  value={field.state.value ?? ""}
                  onValueChange={(value) => field.handleChange(value)}
                  placeholder={tm(
                    "merchant.salesAgents.form.storeIdPlaceholder"
                  )}
                  isLoading={storesLoading}
                  clearable
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>
          <DialogFooter className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? <ButtonSpinner /> : null}
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    <MutationConfirmPreview
      open={preview !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) setPreview(null);
      }}
      data={preview}
      title={t("common.mutationPreview.createTitle")}
      description={t("common.mutationPreview.createDescription")}
      t={tm}
      pending={pending}
      onConfirm={() => {
        if (!preview) return;
        createAgent.mutate(
          { body: preview, actingMerchantId },
          {
            onSuccess: () => {
              notifSilence(tm("merchant.salesAgents.feedback.created"));
              setPreview(null);
              onOpenChange(false);
            },
            onError: (error) => notifSilenceError(error.message),
          }
        );
      }}
    />
    </>
  );
}
